import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Accordion } from 'react-bootstrap';
import { groupKey, jobKey } from './expandKeys';
import { FaBriefcase, FaChevronDown, FaChevronRight, FaTasks } from 'react-icons/fa';

interface JobCardProps {
  job: any;
  packageId: string | number | null;
  canvas: boolean;
}

const DetailCard: React.FC<JobCardProps> = ({ job, packageId, canvas }) => {
  const jobsToRender = Array.isArray(job) ? job : [job];

  const seenJobs = useRef<Set<number | string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<number | string>>(() => {
    const initial = new Set<number | string>();
    jobsToRender.forEach((item: any) => {
      if (item?.job_id != null) {
        seenJobs.current.add(item.job_id);
        initial.add(jobKey(item.job_id));
      }
    });
    return initial;
  });

  useEffect(() => {
    const fresh = jobsToRender.filter((item: any) => item?.job_id != null && !seenJobs.current.has(item.job_id));
    if (fresh.length === 0) return;
    fresh.forEach((item: any) => seenJobs.current.add(item.job_id));
    setExpandedItems(prev => {
      const next = new Set(prev);
      fresh.forEach((item: any) => next.add(jobKey(item.job_id)));
      return next;
    });
  }, [job]);

  const togglePanel = (id: string | number) => {
    setExpandedItems(prev => {
      const newExpanded = new Set(prev);
      newExpanded.has(id) ? newExpanded.delete(id) : newExpanded.add(id);
      return newExpanded;
    });
  };

  const groupJobsByPackageId = (jobs: any[]) => {
    return jobs.reduce((acc: any, job: any) => {
      const pid = job?.packageId;
      if (!acc[pid]) acc[pid] = [];
      acc[pid].push(job);
      return acc;
    }, {});
  };

  const groupedJobs = packageId ? groupJobsByPackageId(jobsToRender) : { "": jobsToRender };

  return (
    <Accordion className="metadata-package-details">
      {Object.keys(groupedJobs).map((pkgId) => (
        <div key={pkgId} className="metadata-package-details__group">
          {!canvas && packageId && <h5 className="metadata-package-details__packageId">Package ID: {pkgId}</h5>}

          {groupedJobs[pkgId].map((singleJob: any) => (
            <Card key={singleJob?.job_id} className="metadata-package-detail-card">
              <Card.Header className="metadata-package-detail-card__header">
                <div className="metadata-package-detail-card__rail" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>

                <div className="metadata-package-detail-card__content">
                  <span className="metadata-package-detail-card__avatar">
                    <FaBriefcase />
                  </span>
                  <div className="metadata-package-detail-card__titleBlock">
                    <h5>{singleJob?.job_name}</h5>
                    <span>{singleJob?.job_task_groups?.map((g: any) => g?.job_task_group_name).join(', ')}</span>
                  </div>
                </div>
                <Button className="metadata-package-detail-card__toggle" variant="link" onClick={() => togglePanel(jobKey(singleJob?.job_id))}>
                  {expandedItems.has(jobKey(singleJob?.job_id)) ? <FaChevronDown /> : <FaChevronRight />}
                </Button>
              </Card.Header>

              <Accordion.Collapse eventKey={String(singleJob?.job_id)} in={expandedItems.has(jobKey(singleJob?.job_id))}>
                <Card.Body className="metadata-package-detail-card__body">
                  {singleJob?.job_task_groups?.map((group: any) => (
                    <Accordion key={group?.job_task_group_id} className="metadata-package-group">
                      <Card className="metadata-package-group__card">
                        <Card.Header className="metadata-package-group__header">
                          <div>
                            <span className="metadata-package-group__eyebrow">Task Group</span>
                            <h6>{group?.job_task_group_name}</h6>
                          </div>
                          <Button className="metadata-package-group__toggle" variant="link" onClick={() => togglePanel(groupKey(group.job_task_group_id))}>
                            {expandedItems.has(groupKey(group?.job_task_group_id)) ? <FaChevronDown /> : <FaChevronRight />}
                          </Button>
                        </Card.Header>
                        <Accordion.Collapse eventKey={String(group.job_task_group_id)} in={expandedItems.has(groupKey(group.job_task_group_id))}>
                          <Card.Body className="metadata-package-group__body">
                            {group?.job_tasks?.map((task: any) => (
                              <div key={task?.job_task_id} className="metadata-package-task">
                                <span className="metadata-package-task__icon"><FaTasks /></span>
                                <div className="metadata-package-task__content">
                                  <strong>{task?.job_task_name}</strong>
                                  <span>
                                    {task?.job_steps?.length > 0 && task.job_steps[0].job_step_name}
                                    {(task.job_task_target_entity_table_construct || task.target_entity_table_constructor) &&
                                      ` · ${task.job_task_target_entity_table_construct ?? task.target_entity_table_constructor}`}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </Card.Body>
                        </Accordion.Collapse>
                      </Card>
                    </Accordion>
                  ))}
                </Card.Body>
              </Accordion.Collapse>
            </Card>
          ))}
        </div>
      ))}
    </Accordion>
  );
};

export default DetailCard;
